'use client';

import { ReactNode } from 'react';
import { ProPlanId } from '@domain/premium/plans';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { useCurrentURL } from '@hooks/useCurrentURL';
import { EventMykissCtaButtonClicked } from '@generated/events';
import {
  companiesURLParameters as onboardingCompaniesURLParameters,
  flowsURLParameters as onboardingFlowsURLParameters,
  URLParameters as onboardingURLParameters,
} from '../../../../views/Onboarding/constants/url';
import { NonCrawlableLink } from '../../NonCrawlableLink/NonCrawlableLink';
import { ClickableSize, ClickableVariant } from '../../variants';

import styles from './CTAButton.module.css';

interface IProps {
  type: 'pro';
  proPlanId?: ProPlanId;
  /* A unique id for the location where the signup CTA is, e.g. "waters_sidebar", "blog_post_footer" */
  source: EventMykissCtaButtonClicked['properties']['source'];
  /* If the source is a datatype (e.g. a water, a species), then this is the external ID of that datatype */
  sourceId?: EventMykissCtaButtonClicked['properties']['source_id'];
  onClick?: (event?: any) => void;
  children: ReactNode;
  className?: string;
  variant?: ClickableVariant;
  size?: ClickableSize;
  routerPushTo?: string;
}

/**
  CTAButton wraps common call to action buttons (pro) and fires an analytics event when
  called. It also prepends a Fishbrain Pro logo to the button.

  We use a non crawlable link here so that google bot cannot find and 'press' these buttons. If that
  happens google will index all of the random query string urls in our crawl budget.
 */

export const CTAButton = ({
  type,
  proPlanId,
  source,
  sourceId,
  onClick,
  children,
  className,
  routerPushTo,
  ...props
}: IProps) => {
  const { currentPage } = useAnalytics();
  const { addParameters } = useCurrentURL();

  const becomeProLink = addParameters({
    [onboardingURLParameters.Company]: onboardingCompaniesURLParameters.fishbrain,
    [onboardingURLParameters.Flow]: onboardingFlowsURLParameters.fishbrain.becomePro,
  });

  return (
    <NonCrawlableLink
      routerPushTo={routerPushTo ?? becomeProLink}
      onClickEvent={event => {
        trackEvent({
          name: 'mykiss_cta_button_clicked',
          properties: {
            type: 'pro',
            source,
            source_id: sourceId,
            page: currentPage,
          },
        });
        if (onClick) {
          onClick(event);
        }
      }}
      className={className}
      ariaLabel="become a Fishbrain Pro member"
      {...props}
    >
      <div className={styles.starIcon} />
      {children}
    </NonCrawlableLink>
  );
};
